import React, { useEffect, useRef, useState } from 'react'
import { PersonDto, PersonLiteDto } from '../../../api/person/models/PersonDto'
import personApi from '../../../api/person/PersonApi'
import Card from '../../Card'
import PersonUi from '../../PersonUi'
import LoadingSpinner from '../../LoadingSpinner'
import Note from '../../Note'
import { NoteType } from '../../../enums/NoteType'
import ExpandableText from '../../ExpandableText'
import { formatText, getCountryFlag } from '../../../utils/personUtils'
import { ConnectionDetailLiteDto } from '../../../api/network/models/getNetworkScopedDtos'
import HelixJobTimeline from '../HelixJobTimeline'
import HelixCandidateConnectionDetails from '../HelixCandidateConnectionDetails'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import { MapExtra } from '../../../utils/MapExtra'
import {
    TransformedCompany,
    TransformedConnection,
    TransformedResolvedPerson,
} from '../HelixCandidateResultList'
import { SharedWorkHistoryLiteDto } from '../../../api/role/models/SharedWorkHistoryDto'

type Props = {
    candidateId: string
    networkLinksPersonLiteCache: MapExtra<string, PersonLiteDto>
    candidatesPersonCache: MapExtra<string, PersonDto>
    companyCache: MapExtra<string, CompanyDto>
    candidateConnections: ConnectionDetailLiteDto[]
}

export default function HelixCandidateRightPane({
    candidateId,
    networkLinksPersonLiteCache,
    candidatesPersonCache,
    companyCache,
    candidateConnections,
}: Props): React.JSX.Element {
    const [person, setPerson] = useState<PersonDto | undefined>(undefined)
    const [personLite, setPersonLite] = useState<PersonLiteDto | undefined>(
        undefined,
    )
    const prevSelectedCandidateIdRef = useRef<string | undefined>(undefined)
    const [errMsg, setErrMsg] = useState<string | undefined>()

    useEffect(() => {
        const fetchPersonData = async () => {
            try {
                const personLite = candidatesPersonCache.get(candidateId)
                if (!personLite)
                    throw new Error(
                        `Missing PersonLite in cache for ${candidateId}`,
                    )

                setPersonLite(personLite)

                const response = await personApi.getPersonById(personLite.id)
                if (response.person.id === candidateId) {
                    setPerson(response.person)
                }
            } catch (err) {
                console.error(err)
                setErrMsg(
                    'There was a problem loading the candidate. Please refresh and try again.',
                )
            }
        }

        if (
            prevSelectedCandidateIdRef.current &&
            candidateId === prevSelectedCandidateIdRef.current
        ) {
            return
        }

        prevSelectedCandidateIdRef.current = candidateId

        setPerson(undefined)
        setPersonLite(undefined)
        setErrMsg(undefined)

        fetchPersonData()
    }, [candidateId, candidatesPersonCache])

    if (errMsg) {
        return (
            <Note type={NoteType.ERROR} containerClassNames="bg-white">
                {errMsg}
            </Note>
        )
    }

    // TODO : Extract function as it's duplicated
    function transformToTransformedResolvedPerson(
        personId: string,
        connectionDetails: ConnectionDetailLiteDto[],
    ): TransformedResolvedPerson {
        const now = new Date()

        const companyMap = new Map<
            string,
            Map<string, SharedWorkHistoryLiteDto[]>
        >()

        for (const connection of connectionDetails) {
            const seedPersonId = connection.seedPersonId

            for (const swh of connection.sharedWorkHistory) {
                const companyId = swh.companyId

                const seedMap =
                    companyMap.get(companyId) ??
                    new Map<string, SharedWorkHistoryLiteDto[]>()

                const existing = seedMap.get(seedPersonId) ?? []
                existing.push(swh)

                seedMap.set(seedPersonId, existing)
                companyMap.set(companyId, seedMap)
            }
        }

        const companies: TransformedCompany[] = Array.from(
            companyMap.entries(),
        ).map(([companyId, seedMap]) => {
            const connections: TransformedConnection[] = Array.from(
                seedMap.entries(),
            ).map(([seedPersonId, history]) => {
                const resolvedTitles = new Set<string>()
                const seedTitles = new Set<string>()
                const overlaps: { start: Date; end: Date }[] = []

                for (const swh of history) {
                    if (
                        !swh.resolvedPersonWorkedAt.startDate ||
                        !swh.seedPersonWorkedAt.startDate
                    ) {
                        continue
                    }

                    resolvedTitles.add(swh.resolvedPersonWorkedAt.jobTitle)
                    seedTitles.add(swh.seedPersonWorkedAt.jobTitle)

                    const start = new Date(swh.startOfOverlap ?? now)
                    const end = new Date(swh.endOfOverlap ?? now)

                    overlaps.push({ start, end })
                }

                let cumulativeOverlapMs = 0
                let earliestStart = now
                let latestEnd = new Date(0)

                for (const { start, end } of overlaps) {
                    cumulativeOverlapMs += end.getTime() - start.getTime()
                    if (start < earliestStart) earliestStart = start
                    if (end > latestEnd) latestEnd = end
                }

                return {
                    seedPersonId,
                    resolvedPersonJobTitles: Array.from(resolvedTitles),
                    seedPersonJobTitles: Array.from(seedTitles),
                    cumulativeOverlapMs,
                    overallStart: earliestStart,
                    overallEnd: latestEnd,
                }
            })

            // Sort connections by latest overlap end date
            connections.sort((a, b) => {
                const aEnd = a.overallEnd?.getTime() ?? 0
                const bEnd = b.overallEnd?.getTime() ?? 0
                return bEnd - aEnd
            })

            return {
                companyId,
                connections,
            }
        })

        // Sort companies by most recent connection's overall end date
        companies.sort((a, b) => {
            const aMax = Math.max(
                ...a.connections.map((c) => c.overallEnd.getTime()),
            )
            const bMax = Math.max(
                ...b.connections.map((c) => c.overallEnd.getTime()),
            )
            return bMax - aMax
        })

        return {
            personId,
            companies,
        }
    }

    return (
        <div>
            {personLite && (
                <Card
                    classes="bg-white"
                    footer={
                        <div className="flex mt-[-0.5rem] flex-col">
                            <div>{person?.headline}</div>
                            <div className="text-gray-600 text-sm">
                                {getCountryFlag(
                                    person?.location
                                        ?.split(',')
                                        .pop()
                                        ?.trim() ?? '',
                                )}
                                <span className="ml-2">{person?.location}</span>
                            </div>
                        </div>
                    }
                >
                    <div className="flex mb-2 justify-between">
                        <PersonUi
                            person={personLite}
                            showCurrentJobDetails={false}
                            nameFontSize="text-[1.6rem]"
                            imageClasses="w-[130px] h-[130px]"
                        />
                        {!person && <LoadingSpinner show={true} />}
                    </div>
                </Card>
            )}

            {person &&
                candidateConnections &&
                candidateConnections.length > 0 && (
                    <div className="border rounded-lg p-4 bg-white border-gray-300 mt-2">
                        <h2 className="text-lg font-semibold mb-2">
                            Connections
                        </h2>
                        <div className="text-sm">
                            <HelixCandidateConnectionDetails
                                transformedResolvedPerson={transformToTransformedResolvedPerson(
                                    candidateId,
                                    candidateConnections,
                                )}
                                networkLinksPersonLiteCache={
                                    networkLinksPersonLiteCache
                                }
                                companyCache={companyCache}
                            />
                        </div>
                    </div>
                )}

            {person?.summary && (
                <div className="border rounded-lg p-4 bg-white border-gray-300 mt-2">
                    <h2 className="text-lg font-semibold mb-2">About</h2>
                    <ExpandableText
                        className="text-sm whitespace-pre-line text-gray-600"
                        text={formatText(person.summary)}
                        maxLines={4}
                    />
                </div>
            )}

            {person?.workedAts && person.workedAts.length > 0 && (
                <div className="border rounded-lg bg-white border-gray-300 mt-2">
                    <div className="mt-4">
                        <HelixJobTimeline jobs={person.workedAts} />
                    </div>
                </div>
            )}
        </div>
    )
}
